import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Progress,
  Table,
  Statistic,
  Space,
  Button,
  Badge,
  Slider,
  Tooltip,
  theme,
  Spin,
  message,
  Modal,
} from "antd";
import {
  ThunderboltOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ArrowLeftOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SendOutlined,
  HistoryOutlined,
  GlobalOutlined,
  DashboardOutlined,
  BulbOutlined,
  HomeOutlined,
  CloudOutlined,
  DatabaseOutlined,
  ApiOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  Line,
  ComposedChart,
} from "recharts";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import request from "@/libs/request";
import { authenticatedFetch } from "@/libs/api";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;

// Timezone display labels
const TIMEZONE_LABELS = {
  "Australia/Brisbane": "AEST",
  "Australia/Sydney": "Sydney",
  "Australia/Adelaide": "Adelaide",
  "Australia/Perth": "Perth",
  "Australia/Melbourne": "Melbourne",
};

export default function CurrentDispatchDetail() {
  const { token } = theme.useToken();
  const { dispatchId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dispatch, setDispatch] = useState(null);
  const [deviceDispatches, setDeviceDispatches] = useState([]);
  const [realtimeCurves, setRealtimeCurves] = useState(null);
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [realtimeData, setRealtimeData] = useState([]);
  const [powerTarget, setPowerTarget] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Fetch dispatch detail from API
  const fetchDispatch = useCallback(async () => {
    if (!dispatchId) return;
    try {
      const res = await request(`/api/dispatch/${dispatchId}`, { method: 'GET' });
      if (res?.success && res?.data?.dispatch) {
        setDispatch(res.data.dispatch);
        if (res.data.deviceDispatches) {
          setDeviceDispatches(res.data.deviceDispatches);
        }
        if (res.data.realtimeCurves) {
          setRealtimeCurves(res.data.realtimeCurves);
        }
      }
    } catch (err) {
      console.error("Failed to fetch dispatch:", err);
      message.error("Failed to load dispatch details");
    } finally {
      setLoading(false);
    }
  }, [dispatchId]);

  useEffect(() => {
    fetchDispatch();
    // Refresh every 30 seconds for running dispatches
    const interval = setInterval(fetchDispatch, 30000);
    return () => clearInterval(interval);
  }, [fetchDispatch]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Process realtime curves from Cassandra (288-point arrays = 5-min intervals)
  // Time labels are in AEST (Australia/Brisbane = UTC+10)
  useEffect(() => {
    const targetPower = dispatch?.targetPowerKw || 0;

    if (realtimeCurves && realtimeCurves.pbat288 && realtimeCurves.pbat288.length > 0) {
      // Use real data from Cassandra powow_np_group_realtime_day table
      const pbat288 = realtimeCurves.pbat288;
      const pv288 = realtimeCurves.pv288 || [];
      const load288 = realtimeCurves.load288 || [];
      const pmeter288 = realtimeCurves.pmeter288 || [];
      const dataLength = pbat288.length;

      // Determine time labels based on dispatch context
      const startPeriodId = realtimeCurves.dispatchStartPeriodId || 1;
      const datesIncluded = realtimeCurves.datesIncluded || [];
      const dispatchStartDate = realtimeCurves.dispatchStartDate;

      // Convert data to chart format with AEST time labels
      const data = [];
      for (let i = 0; i < dataLength; i++) {
        // Calculate AEST time label
        // Period ID: 1 = 00:00, 2 = 00:05, ... 288 = 23:55 (in AEST)
        let timeStr;
        if (datesIncluded.length <= 1) {
          // Single day - calculate AEST time from period
          const actualPeriod = startPeriodId + i;
          const hours = Math.floor(((actualPeriod - 1) % 288) / 12);
          const minutes = ((actualPeriod - 1) % 12) * 5;
          timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        } else {
          // Multi-day dispatch - show date + time in AEST
          // Calculate which day and period this interval falls on
          let remainingIntervals = i;
          let dayIndex = 0;
          let periodInDay = startPeriodId;

          // First day: from startPeriodId to 288
          const firstDayIntervals = 288 - startPeriodId + 1;
          if (remainingIntervals < firstDayIntervals) {
            periodInDay = startPeriodId + remainingIntervals;
          } else {
            remainingIntervals -= firstDayIntervals;
            dayIndex = 1 + Math.floor(remainingIntervals / 288);
            periodInDay = (remainingIntervals % 288) + 1;
          }

          const hours = Math.floor((periodInDay - 1) / 12);
          const minutes = ((periodInDay - 1) % 12) * 5;

          // Show date prefix for multi-day (MM/DD HH:mm)
          if (datesIncluded[dayIndex]) {
            const dateStr = datesIncluded[dayIndex].slice(5); // MM-DD
            timeStr = `${dateStr} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
          } else {
            timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
          }
        }

        // For future periods (null values), keep as null so chart shows gaps
        // Only convert 0 values to 0, not null to 0
        const getValue = (val) => val === null || val === undefined ? null : val;

        data.push({
          time: timeStr,
          power: getValue(pbat288[i]), // Battery power (positive = discharge, negative = charge)
          pv: getValue(pv288[i]),
          load: getValue(load288[i]),
          pmeter: getValue(pmeter288[i]), // Grid power (positive = export, negative = import)
          target: targetPower > 0 ? targetPower : null, // Only show target if set
        });
      }

      setRealtimeData(data);
    } else {
      // No Cassandra data - show empty state
      setRealtimeData([]);
    }
  }, [dispatch?.targetPowerKw, realtimeCurves]);

  // Calculate progress based on real timestamps
  const getProgressInfo = () => {
    if (!dispatch) return { elapsed: 0, duration: 300, percent: 0 };

    const startTime = dispatch.actualStart || dispatch.scheduledStart;
    const endTime = dispatch.scheduledEnd;

    if (!startTime) return { elapsed: 0, duration: 300, percent: 0 };

    const start = dayjs(startTime);
    const end = endTime ? dayjs(endTime) : start.add(5, "minute");
    const now = dayjs();

    const totalDuration = end.diff(start, "second");
    const elapsed = Math.max(0, Math.min(totalDuration, now.diff(start, "second")));
    const percent = Math.min(100, (elapsed / totalDuration) * 100);

    return { elapsed, duration: totalDuration, percent };
  };

  const progressInfo = getProgressInfo();

  // Build active dispatch object from real data
  const groupTimezone = dispatch?.groupLocalTimezone || "Australia/Adelaide";

  // Calculate max FCAS values from raise/lower markets
  const getFcasMax = (markets) => {
    if (!markets || !Array.isArray(markets)) return 0;
    return Math.max(...markets.filter(v => typeof v === 'number'), 0);
  };

  // FCAS market data from AEMO Cassandra (via API)
  // fcasRaiseMarkets/fcasLowerMarkets: [1s, 6s, 60s, 5min] in MW
  const fcasRaiseMarkets = dispatch?.fcasRaiseMarkets || [0, 0, 0, 0];
  const fcasLowerMarkets = dispatch?.fcasLowerMarkets || [0, 0, 0, 0];
  const maxFcasRaise = getFcasMax(fcasRaiseMarkets);
  const maxFcasLower = getFcasMax(fcasLowerMarkets);

  const isFcasDispatch = dispatch?.dispatchType === 'FCAS' || dispatch?.strategyName?.includes('FCAS');

  const activeDispatch = dispatch ? {
    id: dispatch.id,
    type: dispatch.dispatchType || "MANUAL",
    market: dispatch.strategyName || "Manual Dispatch",
    startTime: dispatch.scheduledStartLocal || dispatch.scheduledStart,
    expectedEnd: dispatch.scheduledEndLocal || dispatch.scheduledEnd,
    startTimeAest: dispatch.scheduledStart,
    expectedEndAest: dispatch.scheduledEnd,
    duration: progressInfo.duration,
    elapsed: progressInfo.elapsed,
    targetPower: dispatch.targetPowerKw || 0,
    currentPower: dispatch.actualPowerKw || 0,
    headroom: dispatch.headroomKw || 5000,
    footroom: dispatch.footroomKw ? -Math.abs(dispatch.footroomKw) : -3000,
    totalSystems: dispatch.totalDevices || 0,
    respondingSystems: dispatch.participatingDevices || dispatch.onlineDevices || 0,
    groupLocalTimezone: groupTimezone,
    controlGroupName: dispatch.controlGroupName,
    // FCAS specific
    fcasRaise: maxFcasRaise,
    fcasLower: maxFcasLower,
    avgFrequency: dispatch?.avgFrequency || 50.0,
    isFcas: isFcasDispatch,
  } : {
    id: dispatchId || "Loading...",
    type: "-",
    market: "-",
    startTime: "-",
    expectedEnd: "-",
    duration: 300,
    elapsed: 0,
    targetPower: 0,
    currentPower: 0,
    headroom: 5000,
    footroom: -3000,
    totalSystems: 0,
    respondingSystems: 0,
    groupLocalTimezone: "Australia/Adelaide",
    controlGroupName: "-",
    fcasRaise: 0,
    fcasLower: 0,
    avgFrequency: 50.0,
    isFcas: false,
  };

  // Build command history and system status from deviceDispatches
  // Each device has lastIntervalIdx pointing to the current/last processed interval
  // We extract the status from onlineStatusIntervals (statuses_288) at that index
  const commandHistory = deviceDispatches.map((device, idx) => {
    const lastIdx = device.lastIntervalIdx;
    // Handle both JSONB arrays (from PostgreSQL) and JS arrays
    const parseJsonbArray = (arr) => {
      if (!arr) return [];
      if (Array.isArray(arr)) return arr;
      if (typeof arr === 'string') {
        try { return JSON.parse(arr); } catch { return []; }
      }
      return [];
    };

    const commandTypes = parseJsonbArray(device.dispatchTypeIntervals);
    const setpoints = parseJsonbArray(device.commandPowerIntervals);
    const statuses = parseJsonbArray(device.onlineStatusIntervals);
    const skipReasons = parseJsonbArray(device.skipReasonIntervals);
    const errorMessages = parseJsonbArray(device.errorMessageIntervals);
    const actualPowers = parseJsonbArray(device.actualPowerIntervals);

    const command = lastIdx >= 0 && lastIdx < commandTypes.length ? commandTypes[lastIdx] : null;
    const setpoint = lastIdx >= 0 && lastIdx < setpoints.length ? setpoints[lastIdx] : null;
    const status = lastIdx >= 0 && lastIdx < statuses.length ? statuses[lastIdx] : null;
    const skipReason = lastIdx >= 0 && lastIdx < skipReasons.length ? skipReasons[lastIdx] : null;
    const errorMessage = lastIdx >= 0 && lastIdx < errorMessages.length ? errorMessages[lastIdx] : null;
    const actualKw = lastIdx >= 0 && lastIdx < actualPowers.length ? actualPowers[lastIdx] : null;

    // Convert interval index to AEST time (period 0 = 00:00, period 1 = 00:05, etc.)
    const intervalToTime = (idx) => {
      if (idx < 0 || idx >= 288) return '-';
      const hours = Math.floor(idx / 12);
      const minutes = (idx % 12) * 5;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    };

    return {
      id: device.id || idx,
      systemSn: device.systemSn,
      intervalIdx: lastIdx,
      timestamp: intervalToTime(lastIdx),
      command: command || '-',
      targetPower: setpoint,
      actualKw,
      status: status || 'pending',
      skipReason,
      errorMessage,
    };
  });

  // System status breakdown based on statuses_288[lastIntervalIdx]
  // sent/success → Responding (green check)
  // failed/error → Failed (red X)
  // others → Other Status (yellow)
  const statusCounts = { responding: 0, failed: 0, other: 0 };
  deviceDispatches.forEach((device) => {
    const lastIdx = device.lastIntervalIdx;
    const parseJsonbArray = (arr) => {
      if (!arr) return [];
      if (Array.isArray(arr)) return arr;
      if (typeof arr === 'string') {
        try { return JSON.parse(arr); } catch { return []; }
      }
      return [];
    };
    const statuses = parseJsonbArray(device.onlineStatusIntervals);
    const status = lastIdx >= 0 && lastIdx < statuses.length ? statuses[lastIdx] : null;

    if (status === 'sent' || status === 'success' || status === 'completed') {
      statusCounts.responding++;
    } else if (status === 'failed' || status === 'error') {
      statusCounts.failed++;
    } else if (status) {
      statusCounts.other++;
    }
  });

  const totalCount = deviceDispatches.length || activeDispatch.totalSystems;
  const systemStatus = [
    { status: "Responding", count: statusCounts.responding, color: token.colorSuccess },
    { status: "Other Status", count: statusCounts.other, color: token.colorWarning },
    { status: "Failed", count: statusCounts.failed, color: token.colorError },
  ];

  // Reason codes (e.g. AI_SELF, NO_AI_PLAN) are shown as-is; sentence-style
  // reasons are truncated with the full text in a tooltip
  const renderReason = (reason) => {
    const text = String(reason);
    if (/^[A-Z0-9_]+$/.test(text)) {
      return <Text type="secondary" style={{ fontSize: 12 }}>{text}</Text>;
    }
    return (
      <Tooltip title={text}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {text.length > 24 ? `${text.slice(0, 24)}…` : text}
        </Text>
      </Tooltip>
    );
  };

  const commandColumns = [
    {
      title: "Interval",
      dataIndex: "intervalIdx",
      key: "intervalIdx",
      width: "10%",
      align: "center",
      render: (idx, record) => (
        <Tooltip title={`Period ${idx} (${record.timestamp} AEST)`}>
          <Tag color="blue">{idx >= 0 ? idx : '-'}</Tag>
        </Tooltip>
      ),
    },
    {
      title: "Time (AEST)",
      dataIndex: "timestamp",
      key: "timestamp",
      width: "12%",
      align: "center",
      render: (t) => t || "-",
    },
    {
      title: "System SN",
      dataIndex: "systemSn",
      key: "systemSn",
      width: "18%",
      align: "center",
    },
    {
      title: "Command",
      dataIndex: "command",
      key: "command",
      width: "15%",
      align: "center",
      render: (cmd) => {
        if (!cmd || cmd === '-') return <Text type="secondary">-</Text>;
        const colors = {
          DISCHARGE: "blue",
          CHARGE: "orange",
          IDLE: "default",
          STANDBY: "default",
        };
        return <Tag color={colors[cmd?.toUpperCase()] || "default"}>{cmd?.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Target (kW)",
      dataIndex: "targetPower",
      key: "targetPower",
      width: "15%",
      align: "center",
      render: (v) => v !== null && v !== undefined ? `${Number(v).toFixed(1)} kW` : "-",
    },
    {
      title: "Actual (kW)",
      dataIndex: "actualKw",
      key: "actualKw",
      width: "12%",
      align: "center",
      render: (v) => v !== null && v !== undefined ? `${Number(v).toFixed(1)} kW` : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "15%",
      align: "center",
      render: (status, record) => {
        if (!status || status === 'pending') {
          return <Tag color="processing">PENDING</Tag>;
        }
        const colors = {
          sent: "success",
          success: "success",
          completed: "success",
          failed: "error",
          error: "error",
          skipped: "warning",
        };
        const tag = <Tag color={colors[status] || "default"}>{status?.toUpperCase()}</Tag>;
        // Step3 writes some skip reasons (kill switch, unsupported vendor) to
        // error_messages_288 rather than skip_reasons_288
        const reason = status === 'skipped'
          ? (record.skipReason || record.errorMessage)
          : status === 'failed' ? record.errorMessage : null;
        if (!reason) return tag;
        return (
          <Space size={4} wrap style={{ justifyContent: "center" }}>
            {tag}
            {renderReason(reason)}
          </Space>
        );
      },
    },
  ];

  const progressPercent = progressInfo.percent;
  // Response rate based on actual device statuses at last_interval_idx
  const responseRate = totalCount > 0
    ? (statusCounts.responding / totalCount) * 100
    : 0;

  const sliderMarks = {
    [activeDispatch.footroom]: {
      label: <span style={{ color: token.colorWarning }}>Footroom<br/>{activeDispatch.footroom} kW</span>,
    },
    0: "0 kW",
    [activeDispatch.headroom]: {
      label: <span style={{ color: token.colorSuccess }}>Headroom<br/>{activeDispatch.headroom} kW</span>,
    },
  };

  const handleDispatch = () => {
    console.log("Dispatching with power:", powerTarget);
  };

  const isPaused = dispatch?.status === "paused";

  // Pause / resume: reversible, unlike Stop on the list page
  const handlePauseResume = () => {
    const action = isPaused ? "resume" : "pause";
    Modal.confirm({
      title: isPaused ? "Resume Dispatch" : "Pause Dispatch",
      content: (
        <div>
          <p><strong>ID:</strong> {activeDispatch.id}</p>
          <p><strong>Control Group:</strong> {activeDispatch.controlGroupName}</p>
          <p style={{ color: "#faad14", marginTop: 12 }}>
            {isPaused
              ? "Commands resume from the next 5-minute interval."
              : "No new commands are sent from the next interval. The last command expires on the devices within 5 minutes. You can resume later."}
          </p>
        </div>
      ),
      okText: isPaused ? "Resume" : "Pause",
      okType: isPaused ? "primary" : "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const response = await authenticatedFetch(`/api/dispatch/${dispatchId}/${action}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          });
          const data = await response.json();
          if (data.success) {
            message.success(isPaused ? "Dispatch resumed" : "Dispatch paused");
            fetchDispatch();
          } else {
            message.error(data.message || `Failed to ${action} dispatch`);
          }
        } catch (error) {
          console.error(`Failed to ${action} dispatch:`, error);
          message.error(`Failed to ${action} dispatch`);
        }
      },
    });
  };

  // Get current time in different timezones
  const localTimeLabel = TIMEZONE_LABELS[groupTimezone] || groupTimezone.split("/")[1];
  const localTime = currentTime.tz(groupTimezone);
  const aestTime = currentTime.tz("Australia/Brisbane");

  if (loading) {
    return (
      <PageLayout active="dispatchCurrent">
        <div style={{ textAlign: "center", padding: 100 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading dispatch details...</div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout active="dispatchCurrent">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/dispatch/current")}
              style={{ marginBottom: 8, paddingLeft: 0 }}
            >
              Back to List
            </Button>
            <Title level={2} style={{ marginBottom: 4 }}>
              <Badge status={isPaused ? "warning" : "processing"} />
              <span style={{ marginLeft: 8 }}>Dispatch Detail</span>
              {isPaused && <Tag color="warning" style={{ marginLeft: 12, verticalAlign: "middle" }}>Paused</Tag>}
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              {activeDispatch.id}
            </Text>
            {activeDispatch.controlGroupName && (
              <Tag color="blue" style={{ marginLeft: 8 }}>{activeDispatch.controlGroupName}</Tag>
            )}
          </div>
          {/* Time Display - Local Time + AEST */}
          <div style={{ textAlign: "right" }}>
            <div style={{ marginBottom: 8 }}>
              <Space size={4}>
                <GlobalOutlined style={{ color: token.colorPrimary }} />
                <Text type="secondary" style={{ fontSize: 12 }}>{localTimeLabel}</Text>
              </Space>
              <Title level={3} style={{ margin: 0, fontFamily: "monospace" }}>
                {localTime.format("HH:mm:ss")}
              </Title>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>AEST</Text>
              <div style={{ fontFamily: "monospace", fontSize: 18, color: token.colorTextSecondary }}>
                {aestTime.format("HH:mm:ss")}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Dispatch Banner */}
      <Card
        bordered={false}
        style={{
          borderRadius: 12,
          marginBottom: 24,
          background: `linear-gradient(135deg, ${token.colorPrimary}15 0%, ${token.colorSuccess}15 100%)`,
          border: `2px solid ${token.colorPrimary}`,
        }}
      >
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={8}>
            <Space direction="vertical" size={4}>
              <Text type="secondary">Active Dispatch</Text>
              <Title level={3} style={{ margin: 0, color: token.colorPrimary }}>
                {activeDispatch.id}
              </Title>
              <Space>
                <Tag color="blue" icon={<ThunderboltOutlined />}>
                  {activeDispatch.type}
                </Tag>
                <Tag color="purple">{activeDispatch.market}</Tag>
              </Space>
            </Space>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ textAlign: "center" }}>
              <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                Progress
              </Text>
              <Progress
                percent={progressPercent}
                status="active"
                strokeColor={{
                  "0%": token.colorPrimary,
                  "100%": token.colorSuccess,
                }}
                format={() => `${Math.floor(activeDispatch.elapsed / 60)}:${String(activeDispatch.elapsed % 60).padStart(2, "0")}`}
              />
              {/* Time range in local timezone */}
              <div style={{ fontSize: 12, marginTop: 8, background: token.colorPrimaryBg, padding: "4px 8px", borderRadius: 4 }}>
                <Text type="secondary">
                  {localTimeLabel}: {activeDispatch.startTime && dayjs(activeDispatch.startTime).isValid() ? dayjs(activeDispatch.startTime).format("YYYY-MM-DD HH:mm") : "-"} ~ {activeDispatch.expectedEnd && dayjs(activeDispatch.expectedEnd).isValid() ? dayjs(activeDispatch.expectedEnd).format("YYYY-MM-DD HH:mm") : "-"}
                </Text>
              </div>
              {/* Time range in AEST */}
              <div style={{ fontSize: 12, marginTop: 4 }}>
                <Text type="secondary">
                  AEST: {activeDispatch.startTimeAest && dayjs(activeDispatch.startTimeAest).isValid() ? dayjs(activeDispatch.startTimeAest).format("YYYY-MM-DD HH:mm") : "-"} ~ {activeDispatch.expectedEndAest && dayjs(activeDispatch.expectedEndAest).isValid() ? dayjs(activeDispatch.expectedEndAest).format("YYYY-MM-DD HH:mm") : "-"}
                </Text>
              </div>
            </div>
          </Col>
          <Col xs={24} md={8}>
            {activeDispatch.isFcas ? (
              /* FCAS Dispatch - Show Raise/Lower */
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Dispatched FCAS Raise"
                    value={activeDispatch.fcasRaise}
                    suffix="MW"
                    valueStyle={{ fontSize: 24, color: token.colorSuccess }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Dispatched FCAS Lower"
                    value={activeDispatch.fcasLower}
                    suffix="MW"
                    valueStyle={{ fontSize: 24, color: token.colorWarning }}
                  />
                </Col>
              </Row>
            ) : (
              /* Non-FCAS Dispatch - Show Target/Current Power */
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Target Power"
                    value={activeDispatch.targetPower}
                    suffix="kW"
                    valueStyle={{ fontSize: 24 }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Current Power"
                    value={activeDispatch.currentPower}
                    suffix="kW"
                    valueStyle={{ fontSize: 24, color: token.colorSuccess }}
                  />
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Card>

      {/* Realtime Metrics from Cassandra */}
      {realtimeCurves && (
        <Card
          bordered={false}
          style={{ borderRadius: 12, marginBottom: 16 }}
          title={
            <Space>
              <DashboardOutlined style={{ color: token.colorPrimary }} />
              <Text strong>Realtime Metrics</Text>
              <Tag color="green">LIVE</Tag>
              <Tag color="cyan">AEST</Tag>
            </Space>
          }
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              Period {realtimeCurves.lastPeriodId || 0}/288 ({(() => {
                const p = realtimeCurves.lastPeriodId || 0;
                const h = Math.floor((p - 1) / 12);
                const m = ((p - 1) % 12) * 5;
                return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
              })()})
            </Text>
          }
        >
          <Row gutter={[16, 16]}>
            {/* PV Power */}
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title={<Space><BulbOutlined style={{ color: '#faad14' }} /> PV</Space>}
                value={realtimeCurves.latestPvKw || 0}
                precision={1}
                suffix="kW"
                valueStyle={{ fontSize: 20, color: '#faad14' }}
              />
            </Col>
            {/* Load */}
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title={<Space><HomeOutlined style={{ color: '#1890ff' }} /> Load</Space>}
                value={realtimeCurves.latestLoadKw || 0}
                precision={1}
                suffix="kW"
                valueStyle={{ fontSize: 20, color: '#1890ff' }}
              />
            </Col>
            {/* Grid (Pmeter) */}
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title={<Space><ApiOutlined style={{ color: (realtimeCurves.latestPmeterKw || 0) >= 0 ? token.colorSuccess : token.colorWarning }} /> Grid</Space>}
                value={Math.abs(realtimeCurves.latestPmeterKw || 0)}
                precision={1}
                suffix="kW"
                prefix={(realtimeCurves.latestPmeterKw || 0) >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                valueStyle={{ fontSize: 20, color: (realtimeCurves.latestPmeterKw || 0) >= 0 ? token.colorSuccess : token.colorWarning }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {(realtimeCurves.latestPmeterKw || 0) >= 0 ? 'Export' : 'Import'}
              </Text>
            </Col>
            {/* Battery */}
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title={<Space><DatabaseOutlined style={{ color: (realtimeCurves.latestPbatKw || 0) >= 0 ? '#52c41a' : '#ff7a45' }} /> Battery</Space>}
                value={Math.abs(realtimeCurves.latestPbatKw || 0)}
                precision={1}
                suffix="kW"
                prefix={(realtimeCurves.latestPbatKw || 0) >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                valueStyle={{ fontSize: 20, color: (realtimeCurves.latestPbatKw || 0) >= 0 ? '#52c41a' : '#ff7a45' }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {(realtimeCurves.latestPbatKw || 0) >= 0 ? 'Discharge' : 'Charge'}
              </Text>
            </Col>
            {/* SOC */}
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title={<Space><CloudOutlined style={{ color: '#722ed1' }} /> SOC</Space>}
                value={realtimeCurves.latestSoc || 0}
                precision={1}
                suffix="%"
                valueStyle={{ fontSize: 20, color: '#722ed1' }}
              />
            </Col>
            {/* Online Devices */}
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title={<Space><TeamOutlined style={{ color: token.colorSuccess }} /> Online</Space>}
                value={realtimeCurves.latestOnlineCount || 0}
                valueStyle={{ fontSize: 20, color: token.colorSuccess }}
              />
            </Col>
          </Row>

          {/* Headroom / Footroom */}
          <Row gutter={[16, 16]} style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${token.colorBorderSecondary}` }}>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="Headroom"
                value={realtimeCurves.latestHeadroomKw || 0}
                precision={1}
                suffix="kW"
                valueStyle={{ fontSize: 18, color: token.colorSuccess }}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="Footroom"
                value={realtimeCurves.latestFootroomKw || 0}
                precision={1}
                suffix="kW"
                valueStyle={{ fontSize: 18, color: token.colorWarning }}
              />
            </Col>
            {/* Daily Energy Totals */}
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="PV Today"
                value={realtimeCurves.sumPvDayKwh || 0}
                precision={1}
                suffix="kWh"
                valueStyle={{ fontSize: 16, color: '#faad14' }}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="Load Today"
                value={realtimeCurves.sumLoadDayKwh || 0}
                precision={1}
                suffix="kWh"
                valueStyle={{ fontSize: 16, color: '#1890ff' }}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="Grid Today"
                value={Math.abs(realtimeCurves.sumPmeterDayKwh || 0)}
                precision={1}
                suffix="kWh"
                valueStyle={{ fontSize: 16 }}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="Battery Today"
                value={Math.abs(realtimeCurves.sumPbatDayKwh || 0)}
                precision={1}
                suffix="kWh"
                valueStyle={{ fontSize: 16 }}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Row 1: Realtime Power Output - Full Width */}
      <Card
        bordered={false}
        style={{ borderRadius: 12, marginBottom: 16 }}
        title={
          <Space>
            <SyncOutlined spin style={{ color: token.colorSuccess }} />
            <Text strong>Realtime Power Curves</Text>
            <Tag color="cyan">AEST</Tag>
            {realtimeCurves?.datesIncluded?.length > 1 && (
              <Tag color="blue">{realtimeCurves.datesIncluded.length} days</Tag>
            )}
            {/* Show data source: aggregation = batch aggregated, realtime = sparse live data */}
            {realtimeCurves?.dataSource && (
              <Tag color={realtimeCurves.dataSource === 'aggregation' ? 'green' : 'orange'}>
                {realtimeCurves.dataSource === 'aggregation' ? 'Aggregated' : 'Realtime (sparse)'}
              </Tag>
            )}
          </Space>
        }
        extra={
          realtimeCurves && (
            <Space>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {realtimeCurves.dispatchStartDate}
                {realtimeCurves.datesIncluded?.length > 1 && ` ~ ${realtimeCurves.dispatchEndDate}`}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ({realtimeCurves.pv288?.length || 0} intervals)
              </Text>
            </Space>
          )
        }
      >
        <div style={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={realtimeData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
              <defs>
                <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#faad14" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#faad14" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="batGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#52c41a" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#52c41a" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={Math.floor(realtimeData.length / 12) || 1} />
              <YAxis
                tick={{ fontSize: 11 }}
                domain={([dataMin, dataMax]) => {
                  // Ensure minimum range of 10 kW when all data is zero or very small
                  const min = Math.min(dataMin, 0);
                  const max = Math.max(dataMax, 0);
                  if (max - min < 1) {
                    return [-5, 5]; // Default range when no meaningful data
                  }
                  return [min - 1, max + 1]; // Add padding
                }}
              />
              <RechartsTooltip
                contentStyle={{ background: token.colorBgElevated, border: `1px solid ${token.colorBorder}` }}
                formatter={(value, name) => [`${value?.toFixed(1)} kW`, name]}
              />
              <Legend verticalAlign="top" height={36} />
              {/* PV - Yellow area */}
              <Area
                type="monotone"
                dataKey="pv"
                name="PV"
                stroke="#faad14"
                fill="url(#pvGrad)"
                strokeWidth={2}
              />
              {/* Load - Blue line */}
              <Line
                type="monotone"
                dataKey="load"
                name="Load"
                stroke="#1890ff"
                strokeWidth={1.5}
                dot={false}
              />
              {/* Grid - Gray line */}
              <Line
                type="monotone"
                dataKey="pmeter"
                name="Grid"
                stroke="#8c8c8c"
                strokeWidth={1.5}
                dot={false}
                strokeDasharray="3 3"
              />
              {/* Battery - Green area */}
              <Area
                type="monotone"
                dataKey="power"
                name="Battery"
                stroke="#52c41a"
                fill="url(#batGrad)"
                strokeWidth={2}
              />
              {/* Target - Dashed line */}
              <Line
                type="monotone"
                dataKey="target"
                name="Target"
                stroke={token.colorPrimary}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Row 2: FCAS Metrics (only shown for FCAS dispatches) */}
      {activeDispatch.isFcas && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <Card bordered={false} style={{ borderRadius: 12, height: '100%' }}>
              <Statistic
                title={<Text strong>Dispatched FCAS Commands</Text>}
                value={`Raise: ${activeDispatch.fcasRaise} MW`}
                valueStyle={{ fontSize: 20, color: token.colorSuccess }}
              />
              <div style={{ marginTop: 8 }}>
                <Text style={{ fontSize: 16, color: token.colorWarning }}>
                  Lower: {activeDispatch.fcasLower} MW
                </Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card bordered={false} style={{ borderRadius: 12, height: '100%' }}>
              <Statistic
                title={<Text strong>Headroom / Footroom</Text>}
                value={activeDispatch.headroom}
                suffix="kW"
                valueStyle={{ fontSize: 20, color: token.colorSuccess }}
                prefix="HR: "
              />
              <div style={{ marginTop: 8 }}>
                <Text style={{ fontSize: 16, color: token.colorWarning }}>
                  FR: {Math.abs(activeDispatch.footroom).toLocaleString()} kW
                </Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card bordered={false} style={{ borderRadius: 12, height: '100%' }}>
              <Statistic
                title={<Text strong>Average Frequency</Text>}
                value={activeDispatch.avgFrequency}
                precision={3}
                suffix="Hz"
                valueStyle={{
                  fontSize: 20,
                  color: activeDispatch.avgFrequency >= 49.95 && activeDispatch.avgFrequency <= 50.05
                    ? token.colorSuccess
                    : token.colorWarning
                }}
              />
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Normal: 49.95 - 50.05 Hz
                </Text>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Row 3: System Response + Power Adjustment - Side by Side */}
      <Row gutter={[16, 16]}>
        {/* System Response */}
        <Col xs={24} lg={12}>
          <Card
            bordered={false}
            style={{ borderRadius: 12, height: '100%' }}
            title={<Text strong>System Response</Text>}
          >
            <Row gutter={16} align="middle">
              <Col xs={24} sm={10}>
                <div style={{ textAlign: "center" }}>
                  <Progress
                    type="circle"
                    percent={responseRate}
                    format={() => (
                      <div>
                        <div style={{ fontSize: 24, fontWeight: 600 }}>{statusCounts.responding}</div>
                        <div style={{ fontSize: 12, color: token.colorTextSecondary }}>
                          / {totalCount}
                        </div>
                      </div>
                    )}
                    strokeColor={token.colorSuccess}
                    size={100}
                  />
                  <div style={{ marginTop: 8 }}>
                    <Text strong style={{ color: token.colorSuccess }}>{responseRate.toFixed(1)}%</Text>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={14}>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {systemStatus.map((item) => (
                    <div
                      key={item.status}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        background: `${item.color}10`,
                        borderRadius: 8,
                        border: `1px solid ${item.color}30`,
                      }}
                    >
                      <Space>
                        {item.status === "Responding" ? (
                          <CheckCircleOutlined style={{ color: item.color }} />
                        ) : item.status === "Other Status" ? (
                          <SyncOutlined style={{ color: item.color }} />
                        ) : (
                          <CloseCircleOutlined style={{ color: item.color }} />
                        )}
                        <Text>{item.status}</Text>
                      </Space>
                      <Text strong style={{ color: item.color }}>{item.count}</Text>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Power Adjustment */}
        <Col xs={24} lg={12}>
          <Card
            bordered={false}
            style={{ borderRadius: 12, height: '100%' }}
            title={<Text strong>Power Adjustment</Text>}
          >
            <div style={{ padding: "0 8px" }}>
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">Adjust target power (negative = charging)</Text>
              </div>
              <Slider
                min={activeDispatch.footroom}
                max={activeDispatch.headroom}
                value={powerTarget}
                onChange={setPowerTarget}
                marks={sliderMarks}
                tooltip={{
                  formatter: (v) => `${v > 0 ? "+" : ""}${v.toLocaleString()} kW`,
                }}
                styles={{
                  track: {
                    background: powerTarget >= 0 ? token.colorSuccess : token.colorWarning,
                  },
                }}
              />
              <div style={{ textAlign: "center", marginTop: 16 }}>
                <Text strong style={{ fontSize: 24, color: powerTarget >= 0 ? token.colorSuccess : token.colorWarning }}>
                  {powerTarget > 0 ? "+" : ""}{powerTarget.toLocaleString()} kW
                </Text>
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary">{powerTarget >= 0 ? "Discharge" : "Charge"}</Text>
                </div>
              </div>
            </div>

            <Row gutter={8} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Button
                  type="primary"
                  block
                  icon={<SendOutlined />}
                  onClick={handleDispatch}
                  disabled={powerTarget === 0}
                >
                  Dispatch
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  danger={!isPaused}
                  block
                  icon={isPaused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
                  onClick={handlePauseResume}
                  disabled={!dispatch || !["running", "scheduled", "paused"].includes(dispatch.status)}
                >
                  {isPaused ? "Resume" : "Pause"}
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Command History */}
      <Card
        bordered={false}
        style={{ borderRadius: 12, marginTop: 16 }}
        title={
          <Space>
            <HistoryOutlined style={{ color: token.colorPrimary }} />
            <Text strong>Command History</Text>
          </Space>
        }
      >
        <Table
          columns={commandColumns}
          dataSource={commandHistory}
          rowKey="id"
          pagination={makePagination(pageSize, setPageSize)}
          size="middle"
        />
      </Card>
    </PageLayout>
  );
}
